import React from 'react';

import { IS_TEST_ENVIRONMENT } from '#/config';

import { getDefaultOrLastUsedIntervalString } from './chartInterval';
import {
  getChartDisabledFeatures,
  getChartEnabledFeatures,
  getChartOverrides,
} from './chartSettings';
import { ChartProps } from './chartTypes';
import { dataFeed } from './datafeed';
import {
  forgetRenderedOrderLines,
  renderOrders,
  updateOrdersHandlers,
} from './drawings/ordersLines';
import { forgetPositionLine, renderPosition } from './drawings/positionsLine';
import {
  forgetTpSlOrderLine,
  updateCurrentTpSlOrderLine,
} from './drawings/positionTPSLHandle';
import { widget } from './external/charting_library';
import { saveLoadAdapter } from './persistance/chartSaveAdapter';
import { settingsAdapter } from './persistance/settingsAdapter';
import { detectChartTimezone } from './timezones';

import type {
  ChartingLibraryWidgetOptions,
  IChartingLibraryWidget,
  IChartWidgetApi,
} from './external/charting_library.d';

const CONTAINER_ID = 'trading-chart';

const widgetOptions = ({
  baseSymbol,
  quoteSymbol,
  isMobile,
  nightMode,
}: {
  baseSymbol: string;
  quoteSymbol: string;
  isMobile: boolean;
  nightMode: boolean;
}): ChartingLibraryWidgetOptions => ({
  // Enable only for testing chart (see logs in console)
  // debug: true,
  width: '100%' as any,
  height: '100%' as any,
  custom_font_family: 'Monda, monospace',
  // autosize: true,
  symbol: `${baseSymbol}/${quoteSymbol}`,
  timezone: detectChartTimezone(),
  locale: 'en' as const,
  library_path: `${document.location.origin}/static/charting_library/`,
  interval: getDefaultOrLastUsedIntervalString(),
  container: CONTAINER_ID,
  datafeed: dataFeed,
  save_load_adapter: saveLoadAdapter,
  /** A bit annoying is that it loads Last Saved Chart - not Last Open  */
  load_last_chart: true,
  drawings_access: {
    type: 'black' as const,
    tools: [{ name: 'Regression Trend' }],
  },
  // https://github.com/tradingview/charting_library/wiki/Featuresets
  disabled_features: getChartDisabledFeatures(isMobile),
  enabled_features: getChartEnabledFeatures(isMobile),
  theme: nightMode ? ('dark' as const) : ('light' as const),
  // favorites: {
  //   intervals: ['5','15','30','60','120','360','1D','1W'] as ResolutionString[],
  //   chartTypes: ['Candles'],
  // },
  loading_screen: {
    // var(--elevation-light)
    backgroundColor: nightMode ? '#0D111D' : 'white',
  },
  toolbar_bg: nightMode ? '#0D111D' : 'white',
  // numeric_formatting: { decimal_sign: '.' },
  // https://github.com/tradingview/charting_library/wiki/Overrides
  overrides: getChartOverrides(nightMode),

  settings_adapter: settingsAdapter,

  // Must be in `public/static/...`
  custom_css_url: 'modified_css/blueThemeV1.css',
});

/**
 * Chart integration (v1.12 `TradingView.version()`)
 *  https://github.com/tradingview/charting_library/wiki/Widget-Methods
 *  https://github.com/tradingview/charting_library/wiki/Breaking-Changes
 *  https://github.com/tradingview/charting_library/issues/5379
 */
export class ChartComponentCore extends React.PureComponent<ChartProps> {
  private chart: undefined | IChartWidgetApi;

  private widget: undefined | IChartingLibraryWidget;

  private dragging = false;

  changeSymbolWhenReady: (() => void) | null = null;

  componentDidMount() {
    this.renderChart(this.props);
  }

  componentDidUpdate(prevProps: ChartProps) {
    const nextProps = this.props;

    if (!this.widget) {
      console.error('[Chart] Widget not ready');
      return;
    }

    if (prevProps.nightMode !== nextProps.nightMode) {
      try {
        this.widget
          .changeTheme(nextProps.nightMode ? 'dark' : 'light')
          .then(() => {
            if (!this.widget) {
              return;
            }
            this.widget.applyOverrides(getChartOverrides(nextProps.nightMode));
          });
      } catch (error) {
        console.warn(error);
        console.error('[Chart] Cannot change theme');
      }
    }

    // Set the symbol of the new market to display in chart
    if (
      prevProps.baseSymbol !== nextProps.baseSymbol ||
      prevProps.quoteSymbol !== nextProps.quoteSymbol
    ) {
      console.log('[Chart] Changing market', nextProps);
      // when changing markets we want to close any current modals or menus that are open
      try {
        this.changeSymbolWhenReady = () => {
          if (this.chart) {
            this.chart.setSymbol(
              `${nextProps.baseSymbol}/${nextProps.quoteSymbol}`,
              () => {
                console.log('[Chart] ready (changed symbol)');
                try {
                  renderOrders(this.chart, this.props);
                  updateOrdersHandlers(this.chart, this.props);
                  renderPosition(this.chart, this.props, this.dragging);
                } catch (error) {
                  console.error(error);
                }
              }
            );
          }
        };
        if (this.chart && this.widget) {
          this.widget.closePopupsAndDialogs();

          // If chart ready, switch now
          this.changeSymbolWhenReady();
          this.changeSymbolWhenReady = null;
        }
      } catch (error) {
        console.warn(error);
        console.error('[Chart] Cannot change market');
      }
      // Stop processing other events at this case
      // and rerender orders/positions once chart ready (above)
      return;
    }

    if (prevProps.ordersList !== this.props.ordersList) {
      setTimeout(() => {
        renderOrders(this.chart, this.props);
        updateOrdersHandlers(this.chart, this.props);
      });
    } else if (prevProps.onChangeOrderPrice !== this.props.onChangeOrderPrice) {
      setTimeout(() => {
        updateOrdersHandlers(this.chart, this.props);
      });
    }
    if (
      prevProps.position?.entryPrice !== this.props.position?.entryPrice ||
      prevProps.position?.market !== this.props.position?.market ||
      prevProps.position?.quantity !== this.props.position?.quantity ||
      prevProps.position?.unrealizedPnL !== this.props.position?.unrealizedPnL
    ) {
      setTimeout(() => {
        renderPosition(this.chart, this.props, this.dragging);
      });
    }
  }

  componentWillUnmount() {
    this.removeChart();
  }

  chartReadyCallback() {
    if (!this.widget) {
      console.warn('[Chart] Not ready');
      return;
    }
    this.chart = this.widget.activeChart();

    // this.widget.subscribe('drawing_event', (id, type) => {})
    this.widget.subscribe('mouse_down', () => {
      this.dragging = true;
    });
    this.widget.subscribe('mouse_up', () => {
      this.dragging = false;
    });
    this.chart?.crossHairMoved().subscribe(null, () => {
      if (this.dragging && this.props.position) {
        updateCurrentTpSlOrderLine(this.props.position);
      }
    });

    // Update default size of Volume panel
    const studies = this.chart?.getAllStudies();
    if (studies?.length === 1 && studies[0].name === 'Volume') {
      this.chart?.setAllPanesHeight([80, 20]); // ratio in %
    }

    const chartContainer = document.getElementById(CONTAINER_ID);
    if (chartContainer) {
      // Auto size fix.
      chartContainer.style.height = '100%';
    }

    if (this.changeSymbolWhenReady) {
      this.changeSymbolWhenReady();
    }

    this.props.onReady();

    /* When/if the user changes the market via the TradingView
       UI it will currently not change the market data.  This
       can lead to confusing situations where the chart is not
       what they think it is. We now listen to the event and
       set the market in the store when found. */
    // this.chart.onSymbolChanged().subscribe(this, (symbolInfo: any) => {
    //   const [baseSymbol, quoteSymbol] = symbolInfo.ticker.split('_');
    //   if (baseSymbol && quoteSymbol) {
    //     this.props.onMarketSelect(baseSymbol, quoteSymbol);
    //   }
    // });

    // Hotix for settings which are being ignored after chart refresh
    // with new settings (settingsAdapter)
    this.widget.applyOverrides(getChartOverrides(this.props.nightMode));

    renderOrders(this.chart, this.props);
    updateOrdersHandlers(this.chart, this.props);
    renderPosition(this.chart, this.props, this.dragging);
  }

  removeChart() {
    if (this.widget) {
      try {
        this.widget.remove();
        this.widget = undefined;
        this.chart = undefined;
        // Drop stale adapter refs — they belong to the destroyed widget and would
        // be silently reused on the next mount, causing drawings to not render.
        forgetRenderedOrderLines();
        forgetPositionLine();
        forgetTpSlOrderLine();
      } catch (error) {
        console.warn(error);
        console.warn('[Chart] Failed to remove');
      }
    }
  }

  /* TradingView doesn't want us to do this but its because it
     reloads the chart... our other option is to destroy and create.
     It was also removed from version v17 https://github.com/tradingview/charting_library/wiki/Breaking-Changes#version-17
     @see https://github.com/tradingview/charting_library/wiki/Widget-Methods#setlanguagelocale
  */
  // handleLanguageChanged = (_locale: any) => {
  //   const locale = this.getLanguage(); // "en"
  //   try {
  //  // https://github.com/tradingview/charting_library/wiki/Breaking-Changes#version-17
  //  // Widget Method setLanguage(locale) has been removed.
  //     this.widget.setLanguage(locale);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  renderChart({ baseSymbol, quoteSymbol, nightMode, isMobile }: ChartProps) {
    try {
      if (IS_TEST_ENVIRONMENT) {
        return;
      }
      if (!widget) {
        throw new Error('[Chart] CRITICAL ERROR Cannot create chart. err01');
      }
      this.widget = new widget(
        widgetOptions({
          baseSymbol,
          isMobile,
          quoteSymbol,
          nightMode,
        })
      );
      if (!this.widget) {
        throw new Error('[Chart] CRITICAL ERROR Cannot create chart. err02');
      }
      this.widget.onChartReady(this.chartReadyCallback.bind(this));
    } catch (error) {
      console.error(error);
    }
  }

  render() {
    return (
      <div
        id={CONTAINER_ID}
        style={{
          height: '100%',
          width: '100%',
        }}
      />
    );
  }
}
